import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import WalletModal from '../profile/WalletModal';
import WalletLegalIdCard from './WalletLegalIdCard';
import WalletCountryWorkProfilePhoto from './WalletCountryWorkProfilePhoto';
import { MMMMDYYYY } from '../../utils/time';
import personalIcon from '../../img/wallet-documents/personal-icon.svg';
import usworkIcon from '../../img/wallet-documents/uswork-icon.svg';
import style from '../../style/components/wallet/WalletLegalIdDetailModal.module.scss';

const PROFILES = [
  {
    labelKey: 'walletLegalIdDetails.profilePersonal',
    icon: 'personal',
    path: '/profile/personal',
  },
  {
    labelKey: 'walletLegalIdDetails.profileUsWork',
    icon: 'work',
    path: '/profile/uswork',
  },
];

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const PROFILE_ICONS = {
  personal: personalIcon,
  work: usworkIcon,
};

const InfoRows = ({ rows, className }) => {
  const { t } = useTranslation();

  return (
    <div className={`${style.infoRows} ${className}`}>
      {rows.map((row) => (
        <div className={style.infoRow} key={row.labelKey}>
          <span className={style.infoLabel}>{t(row.labelKey)}</span>
          <span className={style.infoValue}>{row.value}</span>
        </div>
      ))}
    </div>
  );
};

InfoRows.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      labelKey: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  className: PropTypes.string,
};

InfoRows.defaultProps = {
  className: '',
};

// The modal's top Personal Information section shows "Full name" and "Date
// of birth" only -- the credential itself has no single fullName field (only
// separate nameFirst/nameMiddle/nameLast), so it's joined here for display.
// This only reshapes what this section renders; details.personalInfo itself
// is left untouched since WalletLegalIdCard's cardholder name still reads
// nameFirst/nameLast from it directly.
const buildTopInfoRows = (personalInfo) => {
  const findValue = (labelKey) =>
    personalInfo?.find((row) => row.labelKey === labelKey)?.value;

  const fullName = [
    findValue('walletLegalIdDetails.nameFirst'),
    findValue('walletLegalIdDetails.nameMiddle'),
    findValue('walletLegalIdDetails.nameLast'),
  ]
    .filter(Boolean)
    .join(' ');
  const dateOfBirth = MMMMDYYYY(findValue('walletLegalIdDetails.dateOfBirth'));

  return [
    fullName && { labelKey: 'walletLegalIdDetails.fullName', value: fullName },
    dateOfBirth && {
      labelKey: 'walletLegalIdDetails.dateOfBirth',
      value: dateOfBirth,
    },
  ].filter(Boolean);
};

// Document Information's issued/expires dates get the same long format as
// Date of birth above; documentNumber/documentIssuer pass through as-is.
const DOCUMENT_DATE_LABEL_KEYS = new Set([
  'walletLegalIdDetails.issued',
  'walletLegalIdDetails.expires',
]);

// Every card shows the same four Document Information fields, in this order.
const DOCUMENT_INFO_LABEL_KEYS = [
  'walletLegalIdDetails.documentNumber',
  'walletLegalIdDetails.issued',
  'walletLegalIdDetails.expires',
  'walletLegalIdDetails.documentIssuer',
];

// Guard MMMMDYYYY, which throws on a truthy-but-invalid date string (date-fns
// rejects an Invalid Date): any unformattable value becomes null -> "N/A".
const formatDocumentDate = (value) => {
  if (!value) return null;
  try {
    return MMMMDYYYY(value);
  } catch {
    return null;
  }
};

// Date of Issue is always shown, as "N/A" when blank -- Green Cards have no
// Date of Issue, EADs do, and both arrive as the same "Residence Permit". The
// other fields keep dropping when blank, so the layout is otherwise unchanged.
const DATE_OF_ISSUE_LABEL_KEY = 'walletLegalIdDetails.issued';

export const buildDocumentInfoRows = (documentInfo, t) => {
  const valueByKey = new Map(
    (documentInfo ?? []).map((row) => [row.labelKey, row.value]),
  );
  const notAvailable = t('walletLegalIdDetails.notAvailable');

  return DOCUMENT_INFO_LABEL_KEYS.map((labelKey) => {
    const raw = valueByKey.get(labelKey);
    const value = DOCUMENT_DATE_LABEL_KEYS.has(labelKey)
      ? formatDocumentDate(raw)
      : raw;
    return { labelKey, value };
  })
    .filter((row) => row.value || row.labelKey === DATE_OF_ISSUE_LABEL_KEY)
    .map((row) => ({ ...row, value: row.value || notAvailable }));
};

const WalletLegalIdDetailModal = ({
  documents,
  selectedIndex,
  isOpen,
  onClose,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const { legalIdDetails, legalIdTitles } = useSelector(
    (state) => state.countryWorkProfile,
  );
  const current = documents[selectedIndex];
  const details = current && legalIdDetails[current.kind];

  if (!current || !details) {
    return null;
  }

  const titleKey = legalIdTitles[current.kind];
  const hasPrev = selectedIndex > 0;
  const hasNext = selectedIndex < documents.length - 1;

  return (
    <WalletModal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      // eslint-disable-next-line react/no-unknown-property -- React does not
      // translate className -> class for genuine custom elements (tags with
      // a hyphen). fragno-modal is one, several layers down through
      // WalletModal -> FragnoModal, so it must be the literal `class` prop.
      class={style.fullScreenModal}
    >
      <div slot="body" className={style.scrollArea}>
        <div className={style.body}>
          <div className={style.headerRow}>
            <div className={style.titleBlock}>
              <h2 className={style.title}>{t(titleKey)}</h2>
              <div className={style.verifiedBadge}>
                <span className={style.dot} />
                {t('shared.verifiedByIdMe')}
              </div>
            </div>
            <button
              type="button"
              className={style.closeButton}
              onClick={onClose}
              aria-label={t('walletLegalIdDetailModal.close')}
            >
              <CloseIcon />
            </button>
          </div>

          <div className={style.cardColumn}>
            <div className={style.cardWrapper}>
              <WalletLegalIdCard variant={current.kind} hideSubtitle />
            </div>
          </div>

          <div className={style.infoColumn}>
            <div className={style.photoRow}>
              <WalletCountryWorkProfilePhoto
                alt={t('walletLegalIdDetailModal.photoAlt')}
                className={style.portrait}
                resetKey={current.kind}
              />
            </div>

            <section className={style.section}>
              <h3 className={style.sectionHeader}>
                {t('walletLegalIdDetailModal.personalInformation')}
              </h3>
              {/* Mobile-only: Figma places the photo beside this card's
                  fields instead of above every section like desktop's
                  .photoRow — kept as a separate element (rather than
                  reused/repositioned) since the two placements differ too
                  much for one element toggled by CSS alone. */}
              <div className={style.personalInfoBody}>
                <InfoRows
                  rows={buildTopInfoRows(details.personalInfo)}
                  className={style.personalInfoRows}
                />
                <WalletCountryWorkProfilePhoto
                  alt={t('walletLegalIdDetailModal.photoAlt')}
                  className={style.portraitMobile}
                  resetKey={current.kind}
                />
              </div>
            </section>

            <section className={style.section}>
              <h3 className={style.sectionHeader}>
                {t('walletLegalIdDetailModal.documentInformation')}
              </h3>
              <InfoRows rows={buildDocumentInfoRows(details.documentInfo, t)} />
            </section>

            <section className={`${style.section} ${style.profilesSection}`}>
              <h3 className={style.sectionHeader}>
                {t('walletLegalIdDetailModal.profiles')}
              </h3>
              <div className={style.profilesList}>
                {PROFILES.map((profile) => (
                  <Link
                    to={profile.path}
                    className={style.profileCard}
                    key={profile.labelKey}
                    onClick={onClose}
                  >
                    <img
                      className={style.profileIcon}
                      src={PROFILE_ICONS[profile.icon]}
                      alt=""
                    />
                    <span className={style.profileLabel}>
                      {t(profile.labelKey)}
                    </span>
                    <ChevronRightIcon />
                  </Link>
                ))}
              </div>
            </section>

            <section className={style.section}>
              <h3 className={style.sectionHeader}>
                {t('walletLegalIdDetailModal.aboutThisCard')}
              </h3>
              <p className={style.about}>{t(details.aboutKey)}</p>
            </section>
          </div>
        </div>

        <div className={style.pagination}>
          <div className={style.paginationPill}>
            <button
              type="button"
              className={style.paginationArrow}
              onClick={() => onNavigate(selectedIndex - 1)}
              disabled={!hasPrev}
              aria-label={t('walletLegalIdDetailModal.previousId')}
            >
              <ChevronLeftIcon />
            </button>
            <span className={style.paginationLabel}>
              {t('walletLegalIdDetailModal.paginationLabel', {
                current: selectedIndex + 1,
                total: documents.length,
              })}
            </span>
            <button
              type="button"
              className={style.paginationArrow}
              onClick={() => onNavigate(selectedIndex + 1)}
              disabled={!hasNext}
              aria-label={t('walletLegalIdDetailModal.nextId')}
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      </div>
    </WalletModal>
  );
};

WalletLegalIdDetailModal.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      kind: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedIndex: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

export default memo(WalletLegalIdDetailModal);
